import React, {
  useEffect,
  useState
} from 'react'
import apiClient from 'src/services/apiClient'
import {Snackbar, Tab, Typography} from '@mui/material'
import SnackbarAlertCustom from 'src/components/elements/SnackbarAlertCustom/SnackbarAlertCustom'
import {STATUS} from 'src/constants/constants'
import Chart from 'src/components/elements/Chart/Chart'
import CardCustom from 'src/components/elements/CardCustom/CardCustom'
import WarningPatientEmptyData from 'src/components/elements/WarningEmptyCard/WarningPatientEmptyData'
import Filters, {FilterItem} from '../../Filters/Filters'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import TabContext from '@mui/lab/TabContext'
import {AnalyticsStyle} from './AnalyticsStyle'

const VITAL_SIGNS_COLLECTION = 'vital-signs-collection'
const THERAPEUTICAL_PARAMS_COLLECTION = 'therapeutic-parameters-collection'

const Analytics: React.FC<{patientId: string}> = ({patientId}) => {
  const [filters, setFilters] = useState<Record<string, any>>({})
  const [maxCompilation, setMaxCompilation] = useState(20)

  const [vitalParamsChartData, setVitalParamsChartData] = useState<Record<string, any>[]>([])
  const [therapeuticalParamsChartData, setTherapeuticalParamsChartData] = useState<Record<string, any>[]>([])
  const [vitalParamsTab, setVitalParamsTab] = useState<boolean>(false)
  const [therapeuticalParamsTab, setTherapeuticalParamsTab] = useState<boolean>(false)
  const [status, setStatus] = useState<any>(undefined)
  const [colors, setColors] = useState<string[]>(['#A5C842', '#495CA2', '#028B96', '#E87D1E', '#003DA5', '#085AA6', '#731472', '#3599B8'])
  const [activeTab, setActiveTab] = useState('0')

  useEffect(() => {
    fetchData()
    fetchProgram()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, filters])

  useEffect(() => {
    if (Object.keys(filters).length > 0) {
      return
    }
    let newMax = 20
    if (activeTab === '0' && vitalParamsChartData.length > 0) {
      newMax = vitalParamsChartData[0]?.data?.length || 20
    } else if (activeTab === '1' && therapeuticalParamsChartData.length > 0) {
      newMax = therapeuticalParamsChartData[0]?.data?.length || 20
    }
    setMaxCompilation(newMax - 1)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, vitalParamsChartData, therapeuticalParamsChartData])

  useEffect(() => {
    // Adjust active tab if current tab has no data
    if (activeTab === '0' && !vitalParamsTab) {
      setActiveTab(therapeuticalParamsTab ? '1' : '0')
    } else if (activeTab === '1' && !therapeuticalParamsTab) {
      setActiveTab(vitalParamsTab ? '0' : '1')
    }
  }, [vitalParamsTab, therapeuticalParamsTab, activeTab])

  const fetchProgram = async () => {
    const program = await apiClient.getProgram()
    if (program) {
      setColors(program.programPalette.colors)
    }
  }

  const fetchData = async () => {
    Promise.all([
      apiClient.getPatientsFeedbacksChartValues(VITAL_SIGNS_COLLECTION, patientId, filters, setStatus),
      apiClient.getPatientsFeedbacksChartValues(THERAPEUTICAL_PARAMS_COLLECTION, patientId, filters, setStatus)
    ])
      .then(([vitalParamsData, therapeuticalParamsData]) => {
        const {startInterval, endInterval} = filters

        const filterColumnChartData = (chartData: Record<string, any>[]) => {
          if (startInterval === undefined || endInterval === undefined) {
            return chartData
          }
          return chartData.map(chart => {
            if ((chart.chartType === 'column' || chart.chartType === 'line') && Array.isArray(chart.data)) {
              const filteredData = chart.data.slice(startInterval, endInterval + 1)
              const filteredXAxis = chart.xAxis ? chart.xAxis.slice(startInterval, endInterval + 1) : undefined
              return {...chart, data: filteredData, xAxis: filteredXAxis}
            }
            return chart
          })
        }

        if (Array.isArray(vitalParamsData) && vitalParamsData.length > 0) {
          const filteredVitalParams = filterColumnChartData(vitalParamsData)
          setVitalParamsChartData(filteredVitalParams)
          setVitalParamsTab(true)
        } else {
          setVitalParamsTab(false)
        }
        if (Array.isArray(therapeuticalParamsData) && therapeuticalParamsData.length > 0) {
          const filteredTherapeuticalParams = filterColumnChartData(therapeuticalParamsData)
          setTherapeuticalParamsChartData(filteredTherapeuticalParams)
          setTherapeuticalParamsTab(true)
        } else {
          setTherapeuticalParamsTab(false)
        }
      })
  }

  const filterConfig: FilterItem[] = [
    {
      id: 'date',
      label: 'Data',
      type: 'rangePicker'
    },
    {
      id: 'compilationInterval',
      label: 'Intervallo di compilazioni',
      type: 'range',
      min: 0,
      max: maxCompilation,
      step: 1,
      getLabel: (value: [number, number]) => `Da T${value[0]} a T${value[1]}`,
      valueLabelFormat: (value: number | undefined) => value === undefined ? '' : `T${value}`
    }
  ]

  const onSubmit = (newFilters: any) => {
    const filtersValues = newFilters.reduce((result: any, item: any) => {
      if (item.type === 'rangePicker') {
        result.startDate = item.value?.[0]
        result.endDate = item.value?.[1]
        return result
      }
      if (item.type === 'range') {
        result.startInterval = item.value?.[0]
        result.endInterval = item.value?.[1]
        return result
      }
      result[item.property] = item.value
      return result
    }, {})
    setFilters(filtersValues)
  }

  const VitalParamsChart = (
    vitalParamsChartData.length ?
      vitalParamsChartData.map((data) => {
        const name = data.unitMeasure ? `${data.name} [${data.unitMeasure}]` : data.name

        return (
          <div key={data.id} style={{marginTop: '8px'}}>
            <CardCustom title={<Typography fontWeight={700} variant='body2'>{name}</Typography>}>
              <Chart
                colors={data.color || colors}
                data={{
                  series: [{...data, data: data.data.map((item: any) => item[1])}],
                  xAxis: data.data.map((item: any) => {
                    const date = new Date(item[0])
                    if (!isNaN(date.getTime())) {
                      const day = String(date.getDate()).padStart(2, '0')
                      const month = String(date.getMonth() + 1).padStart(2, '0')
                      const year = date.getFullYear()
                      return `${day}/${month}/${year}`
                    }
                    return item[0]
                  })
                }}
                type={data.chartType || 'line'}
              />
            </CardCustom>
          </div>
        )
      }) :
      <WarningPatientEmptyData message='Questo paziente non ha nessun parametro vitale associato' />
  )

  const TherapeuticalParamsChart = (
    therapeuticalParamsChartData.length ?
      therapeuticalParamsChartData.map((data) => {
        const name = data.unitMeasure ? `${data.name} [${data.unitMeasure}]` : data.name

        return (
          <div key={data.id} style={{marginTop: '8px'}}>
            <CardCustom title={<Typography fontWeight={700} variant='body2'>{name}</Typography>}>
              <Chart
                colors={data.color || colors}
                data={{
                  series: [{...data, data: data.data.map((item: any) => item[1])}],
                  xAxis: data.data.map((item: any) => {
                    const date = new Date(item[0])
                    if (!isNaN(date.getTime())) {
                      const day = String(date.getDate()).padStart(2, '0')
                      const month = String(date.getMonth() + 1).padStart(2, '0')
                      const year = date.getFullYear()
                      return `${day}/${month}/${year}`
                    }
                    return item[0]
                  })
                }}
                type={data.chartType || 'line'}
              />
            </CardCustom>
          </div>
        )
      }) :
      <WarningPatientEmptyData message='Questo paziente non ha nessun parametro terapeutico associato' />
  )

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue)
  }

  return (
    <AnalyticsStyle>
      {vitalParamsTab || therapeuticalParamsTab ?
        <div style={{display: 'flex', justifyContent: 'flex-end', gap: '8px'}}>
          <Filters config={filterConfig} onSubmit={onSubmit} />
        </div> : null}
      {!vitalParamsTab && !therapeuticalParamsTab ?
        <WarningPatientEmptyData message='Per questo paziente non ci sono dati disponibili' /> :
        <TabContext value={activeTab}>
          <TabList
            onChange={handleTabChange}
            value={activeTab}
          >
            {vitalParamsTab && <Tab label={<Typography style={{fontSize: 12, fontWeight: 600}}>{'Parametri vitali'}</Typography>} value={'0'} />}
            {therapeuticalParamsTab && <Tab label={<Typography style={{fontSize: 12, fontWeight: 600}}>{'Parametri terapeutici'}</Typography>} value={'1'} />}
          </TabList>
          {vitalParamsTab &&
            <TabPanel value={'0'}>
              {VitalParamsChart}
            </TabPanel>}
          {therapeuticalParamsTab &&
            <TabPanel value={'1'}>
              {TherapeuticalParamsChart}
            </TabPanel>}
        </TabContext>}
      <Snackbar
        anchorOrigin={{vertical: 'top', horizontal: 'right'}}
        autoHideDuration={2000}
        onClose={() => setStatus(undefined)}
        open={status === STATUS.REJECTED}
      >
        <div>
          <SnackbarAlertCustom
            onClose={() => setStatus(undefined)}
            severity='error'
            sx={{width: '100%'}}
          >
            <Typography variant='h5'>
              {'Errore nel caricare caregivers'}
            </Typography>
          </SnackbarAlertCustom>
        </div>
      </Snackbar>
    </AnalyticsStyle>
  )
}

export default Analytics
